<script setup lang="ts">
import { ref, onBeforeUnmount, computed, onMounted } from 'vue';
import { AkParagraph } from '@ankorstore/design-system';
import { useI18n } from 'vue-i18n';
import { isEnabled, FeatureFlag } from '@monolith/legacy/services/features';
import { Cms } from '@ankorstore/api-internal/Cms';
import type { GetToplineBannerData } from '@ankorstore/api-internal/data-contracts';

import Analytics from '@monolith/legacy/services/analytics';
import UserClick from '@monolith/legacy/services/analytics/events/user-click.event';
import { captureException } from '@core/plugins/sentry';
import { getLocalized } from '@client/modules/shared/util/locale/get-localized';

type BannerAttributes = GetToplineBannerData['data']['attributes'];

const isVisible = ref(true);
const bannerData = ref<BannerAttributes | null>(null);
const { locale } = useI18n();

const description = computed(() => getLocalized(bannerData.value?.description, locale.value));
const linkUrl = computed(() => getLocalized(bannerData.value?.link_url, locale.value) as string | null);
const linkLabel = computed(() => getLocalized(bannerData.value?.link_label, locale.value) as string | null);
const hasLink = computed(() => !!linkUrl.value && !!linkLabel.value);

const contentStyle = computed(() => {
  if (!bannerData.value) {
    return {};
  }
  return {
    backgroundColor: bannerData.value.background_color,
    color: bannerData.value.text_color,
  };
});

const shouldShowBanner = computed(() => {
  return isEnabled(FeatureFlag.ShowToplineBanner) && !!bannerData.value && !!description.value;
});

function trackUserClick() {
  Analytics.track(
    new UserClick({
      component: 'top_banner',
      action: 'click_hyperlink',
      clicked_value: linkUrl.value ?? '',
    })
  );
}

const handleScroll = () => {
  isVisible.value = window.scrollY <= 56;
};

async function fetchBanner() {
  try {
    const cmsClient = new Cms();
    const response = await cmsClient.getToplineBanner();
    if (response.data) {
      bannerData.value = response.data.attributes;
    }
  } catch (error) {
    if (error instanceof Error) {
      captureException(error);
    }
  }
}

onMounted(() => {
  if (isEnabled(FeatureFlag.ShowToplineBanner)) {
    fetchBanner();
    window.addEventListener('scroll', handleScroll);
  }
});

onBeforeUnmount(() => {
  if (isEnabled(FeatureFlag.ShowToplineBanner)) {
    window.removeEventListener('scroll', handleScroll);
  }
});
</script>

<template>
  <div
    v-if="shouldShowBanner"
    class="banner-top-line"
    :class="{ 'banner-top-line--hidden': !isVisible }"
    data-testid="bannerTopLine"
  >
    <div
      class="banner-top-line__content"
      :style="contentStyle"
      data-testid="bannerTopLineContent"
    >
      <AkParagraph
        data-testid="bannerTopLineDescription"
        class="banner-top-line__description"
        size="sm"
      >
        {{ description }}
        <a
          v-if="hasLink"
          :href="linkUrl!"
          target="_blank"
          class="banner-top-line__link"
          data-testid="bannerTopLineLink"
          @click="trackUserClick"
        >
          {{ linkLabel }}
        </a>
      </AkParagraph>
    </div>
  </div>
</template>

<style lang="scss">
.banner-top-line {
  @apply ds-opacity-100 ds-h-auto ds-transition-all ds-duration-300 ds-ease-in-out ds-order-2 xl:ds-order-1 ds-w-full;
  &--hidden {
    @apply ds-opacity-0 ds-h-0 xl:ds-opacity-100 xl:ds-h-auto;
  }
  &__content {
    @apply ds-inline-flex ds-flex-wrap ds-text-center ds-items-center ds-justify-center ds-py-2 ds-px-4 ds-w-full;
  }
  &__description {
    b,
    strong {
      @apply ds-font-bold;
    }
    span {
      p {
        @apply ds-inline;
      }
    }
  }
  &__link {
    @apply ds-underline;
  }
}
</style>
